import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { companyApi } from './api'
import type {
  TenantBankAccountPayload,
  TenantSettingsPatch,
  TenantSettingsResponse,
} from './types'

export function useTenantSettings() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'tenant-settings'),
    queryFn: companyApi.get,
    // Why: useReportBrand() calls this hook from chat-message renderers
    // (TableBlock, FinancialReportBlock) which also mount on the public
    // /chat/share/:token page. Without this gate the hook fires an authed
    // /api/tenant/... request from an unauthenticated viewer, 401s, and
    // bounces them to /login. Authenticated callers always have a tenantId
    // so this is a no-op for them.
    enabled: !!tenantId,
  })
}

export function useUpdateTenantSettings() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    // Why: every branding / regional / workspace-name save should
    // include the updated_at the form last read so a concurrent edit
    // by another admin returns 409 STALE_VERSION instead of silently
    // overwriting. We read the cached settings response (the same one
    // the page renders) so callers don't have to remember to pass it.
    // Callers can still override by sending expected_updated_at
    // explicitly in the patch.
    mutationFn: (patch: TenantSettingsPatch) => {
      if (patch.expected_updated_at !== undefined) return companyApi.patch(patch)
      const cached = qc.getQueryData<TenantSettingsResponse>(
        tenantKey(tenantId, 'tenant-settings'),
      )
      const expected = cached?.tenant?.updated_at ?? null
      return companyApi.patch({ ...patch, expected_updated_at: expected })
    },
    // Why setQueryData before invalidate: the PATCH response now carries
    // the fresh updated_at. Writing it into the cache synchronously
    // prevents a rapid second save from sending the stale pre-PATCH
    // value (which would 409). The invalidate still re-fetches in the
    // background to pick up any side-effect columns the PATCH didn't
    // return.
    onSuccess: (data) => {
      const cached = qc.getQueryData<TenantSettingsResponse>(
        tenantKey(tenantId, 'tenant-settings'),
      )
      if (data?.tenant) {
        qc.setQueryData<TenantSettingsResponse>(
          tenantKey(tenantId, 'tenant-settings'),
          { tenant: { ...(cached?.tenant ?? {}), ...data.tenant } },
        )
      }
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'tenant-settings') })
    },
  })
}

export function useUploadCompanyLogo() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (file: File) => companyApi.uploadLogo(file),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'tenant-settings') }),
  })
}

function invalidateBankAccounts(
  qc: ReturnType<typeof useQueryClient>,
  tenantId: string | null,
) {
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'tenant-bank-accounts') })
}

export function useTenantBankAccounts() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'tenant-bank-accounts'),
    queryFn: companyApi.listBankAccounts,
  })
}

export function useCreateTenantBankAccount() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: TenantBankAccountPayload) => companyApi.createBankAccount(body),
    onSuccess: () => invalidateBankAccounts(qc, tenantId),
  })
}

export function useUpdateTenantBankAccount() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: TenantBankAccountPayload }) =>
      companyApi.updateBankAccount(id, body),
    onSuccess: () => invalidateBankAccounts(qc, tenantId),
  })
}

export function useDeleteTenantBankAccount() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => companyApi.deleteBankAccount(id),
    onSuccess: () => invalidateBankAccounts(qc, tenantId),
  })
}

export function usePostingConfig() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'tenant-posting-config'),
    queryFn: companyApi.getPostingConfig,
    enabled: !!tenantId,
  })
}

export function useUpdatePostingConfig() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (patch: Partial<import('./api').PostingConfig>) =>
      companyApi.patchPostingConfig(patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'tenant-posting-config') })
    },
  })
}
